import React, { useState } from 'react'
import { AccountPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { GetSSOProviderRequest } from '@trustero/trustero-api-web/lib/account/account_pb'
import { Navigate, useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useCognitoContext } from 'src/context/FormContext/CognitoContext'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { getCognitoAuthGrant } from 'src/Utils/oidc'
import { isValidEmail } from 'src/Utils/validators'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { TextInput } from 'src/components/Reusable/Inputs'
import { NTRCE_API_URL } from '../../../adapter/gRpcAdapter'
import {
  AILogo,
  BrandLogo,
  LoginPageContainer,
  LoginPageContent,
  LoginPageGrid,
  NewLoginPageContainer,
  StyledEmailInput,
  TrusteroLine,
  TrusteroLogo,
  TrusteroLogoBig,
} from '../LoginPage.styles'
import {
  OIDCEmailButton,
  OIDCEmailPageNewContent,
  OIDCEmailNewText,
  OIDCEmailText,
} from './OIDCEmailPage.styles'

export const OIDCEmailPage = (): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const { initialCognitoAuth } = useCognitoContext()
  const [dirty, setDirty] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [error, setError] = useState<string>('')

  const isNewLoginPageEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.NEW_LOGIN_PAGE,
  )
  const backTarget = location.state?.from || '/login'

  if (!initialCognitoAuth) {
    return <Navigate to={location.state?.from || '/login'} replace />
  }

  const onSubmit = async () => {
    setDirty(true)
    if (!isValidEmail(email)) {
      setError('Please enter a valid email')
      return
    }
    setIsLoading(true)
    try {
      const client = new AccountPromiseClient(NTRCE_API_URL)
      const req = new GetSSOProviderRequest().setEmail(email)
      const resp = await client.getSSOProvider(req)
      getCognitoAuthGrant({
        ...initialCognitoAuth,
        provider: resp.getProvider(),
      })
    } catch {
      setError('Provider not found. Please reach out to support@trustero.com')
    } finally {
      setIsLoading(false)
    }
  }

  const onBack = () => {
    navigate(backTarget)
  }

  if (isLoading) {
    const Container = isNewLoginPageEnabled
      ? NewLoginPageContainer
      : LoginPageContainer
    return (
      <Container>
        {isNewLoginPageEnabled ? (
          <OIDCEmailPageNewContent>
            <OIDCEmailNewText>Loading...</OIDCEmailNewText>
          </OIDCEmailPageNewContent>
        ) : (
          <LoginPageGrid>
            <LoginPageContent>
              <OIDCEmailText>Loading...</OIDCEmailText>
            </LoginPageContent>
          </LoginPageGrid>
        )}
      </Container>
    )
  }

  if (isNewLoginPageEnabled) {
    return (
      <NewLoginPageContainer>
        <OIDCEmailPageNewContent>
          <BrandLogo>
            <TrusteroLogoBig /> <AILogo>AI</AILogo>
          </BrandLogo>
          <OIDCEmailNewText>
            Enter your email address to continue logging in with{' '}
            {initialCognitoAuth.provider}
          </OIDCEmailNewText>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSubmit()
            }}
            style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }}
          >
            <StyledEmailInput
              type="text"
              placeholder="Enter Email Address"
              value={email}
              autoComplete="email"
              onChange={(e) => {
                setDirty(true)
                setError('')
                setEmail(e.target.value)
              }}
              autoFocus
              style={{ marginBottom: '2rem' }}
            />
            {error && (
              <p
                style={{
                  color: 'red',
                  marginTop: '-1.5rem',
                  marginBottom: '1.5rem',
                }}
              >
                {error}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: '12px',
              }}
            >
              <StandardButton
                variant={StandardButtonVariant.SECONDARY}
                buttonSize={StandardButtonSize.LARGE}
                type="button"
                onClick={onBack}
                style={{ borderRadius: '12px', width: '160px' }}
              >
                Back
              </StandardButton>
              <StandardButton
                variant={StandardButtonVariant.PRIMARY}
                buttonSize={StandardButtonSize.LARGE}
                type="submit"
                disabled={isLoading}
                style={{ borderRadius: '12px', width: '160px' }}
              >
                Continue
              </StandardButton>
            </div>
          </form>
        </OIDCEmailPageNewContent>
      </NewLoginPageContainer>
    )
  }

  return (
    <LoginPageContainer>
      <LoginPageGrid>
        <LoginPageContent>
          <TrusteroLogo width="100%" height="100%" />
          <TrusteroLine />
          <OIDCEmailText>
            Enter your email address to continue logging in with{' '}
            {initialCognitoAuth.provider}
          </OIDCEmailText>
          <FlexRow gap={16} align={FlexAlign.CENTER}>
            <TextInput
              label="Email"
              name="email"
              initVal={email}
              onChangeCb={(value) => {
                setDirty(true)
                setError('')
                setEmail(value)
              }}
              placeholder="Please enter your email"
              required
              errorMessage={error}
              isValid={!dirty || isValidEmail(email)}
            />
            <FlexRow gap={12} align={FlexAlign.CENTER}>
              <OIDCEmailButton
                buttonSize={StandardButtonSize.MEDIUM}
                variant={StandardButtonVariant.SECONDARY}
                onClick={onBack}
              >
                Back
              </OIDCEmailButton>
              <OIDCEmailButton
                buttonSize={StandardButtonSize.MEDIUM}
                variant={StandardButtonVariant.PRIMARY}
                onClick={onSubmit}
              >
                Continue
              </OIDCEmailButton>
            </FlexRow>
          </FlexRow>
        </LoginPageContent>
      </LoginPageGrid>
    </LoginPageContainer>
  )
}
